import { useRef } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';

/**
 * Configuration for mutation error handling
 */
interface MutationErrorHandlerConfig<T> {
  successMessage: string | ((result: T) => string);
  errorMessage: string;
  onError?: (error: Error, context?: unknown) => void;
}

/**
 * Creates a handler function that wraps a mutation call with standardized error handling.
 * This eliminates duplicate try-catch-toast patterns across mutation handlers.
 *
 * @param mutation - The React Query mutation result
 * @param toast - Toast function from useToast hook
 * @param config - Configuration for success/error messages
 * @returns An async function that executes the mutation with error handling
 *
 * @example
 * const { toast } = useToast();
 * const handleToggle = createMutationHandler(
 *   togglePolicyMutation,
 *   toast,
 *   {
 *     successMessage: (result) => `Policy ${result.policy.name} updated`,
 *     errorMessage: 'Failed to update policy',
 *   }
 * );
 * await handleToggle({ policyKey: 'test', enabled: true });
 */
export function createMutationHandler<TResult, TVariables>(
  mutation: {
    mutateAsync: (variables: TVariables) => Promise<TResult>;
  },
  toast: (options: {
    title: string;
    description: string;
    variant?: 'default' | 'destructive';
  }) => void,
  config: MutationErrorHandlerConfig<TResult>
) {
  return async (variables: TVariables): Promise<TResult> => {
    try {
      const result = await mutation.mutateAsync(variables);
      toast({
        title: 'Success',
        description:
          typeof config.successMessage === 'function'
            ? config.successMessage(result)
            : config.successMessage,
      });
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      console.error(config.errorMessage, error);

      if (config.onError) {
        config.onError(error, variables);
      }

      toast({
        title: 'Error',
        description: config.errorMessage,
        variant: 'destructive',
      });
      throw err;
    }
  };
}

/**
 * Hook to track which item is currently pending in a mutation.
 * Uses a ref to track the current item key, which must be manually set/cleared.
 * Combines with mutation.isPending to determine if a specific item is pending.
 *
 * This is useful when you have multiple items that can be mutated independently
 * and need to show loading states for specific items.
 *
 * @param mutation - The React Query mutation result
 * @returns Object with isPending check function and trackPending/clearPending functions
 *
 * @example
 * const { isPending: isTogglePending, trackPending, clearPending } = useMutationPendingTracker(togglePolicyMutation);
 * // When calling mutation:
 * trackPending(policy.key);
 * try {
 *   await mutation.mutateAsync({ policyKey: policy.key, enabled: true });
 * } finally {
 *   clearPending();
 * }
 * // Check if pending:
 * const saving = isTogglePending(policy.key);
 */
export function useMutationPendingTracker<TVariables>(
  mutation: UseMutationResult<unknown, Error, TVariables>
) {
  const pendingKeyRef = useRef<string | null>(null);

  const isPending = (itemKey: string): boolean => {
    return mutation.isPending && pendingKeyRef.current === itemKey;
  };

  const trackPending = (itemKey: string) => {
    pendingKeyRef.current = itemKey;
  };

  const clearPending = () => {
    pendingKeyRef.current = null;
  };

  return { isPending, trackPending, clearPending };
}

/**
 * Hook to track which items are currently pending in a mutation (for multiple items).
 * Uses a ref to track multiple item keys, which must be manually managed.
 * Combines with mutation.isPending to determine if a specific item is pending.
 *
 * This is useful when you have multiple items that can be mutated independently
 * and need to show loading states for multiple items simultaneously.
 *
 * @param mutation - The React Query mutation result
 * @returns Object with isPending check function and trackPending/clearPending functions
 *
 * @example
 * const { isPending: isSettingsPending, trackPending, clearPending } = useMultiMutationPendingTracker(updateSettingsMutation);
 * // When calling mutation:
 * trackPending(policyKey);
 * try {
 *   await mutation.mutateAsync({ policyKey, settings: {} });
 * } finally {
 *   clearPending(policyKey);
 * }
 * // Check if pending:
 * const saving = isSettingsPending(policyKey);
 */
export function useMultiMutationPendingTracker<TVariables>(
  mutation: UseMutationResult<unknown, Error, TVariables>
) {
  const pendingKeysRef = useRef<Set<string>>(new Set());

  const isPending = (itemKey: string): boolean => {
    return mutation.isPending && pendingKeysRef.current.has(itemKey);
  };

  const trackPending = (itemKey: string) => {
    pendingKeysRef.current.add(itemKey);
  };

  const clearPending = (itemKey: string) => {
    pendingKeysRef.current.delete(itemKey);
  };

  return { isPending, trackPending, clearPending };
}
